import { Deliverable, HttpProject, Workstream } from "tekkr-common/dist/model/project/project";
import React, { createContext, ReactElement, useContext } from "react";
import { useGetProjectQuery } from "./queries/get-project";
import { FullScreenLoader } from "../components/shared/full-screen-loader";

type UseProjectContext = {
    project: HttpProject;
    refetch: () => Promise<void>;
    getWorkstream: (workstreamId: string) => Workstream;
    getDeliverable: (workstreamId: string, deliverableId: string) => Deliverable;
};
const ProjectContext = createContext<UseProjectContext | null>(null);

export function useProject() {
    const value = useContext(ProjectContext);
    if (!value) {
        throw new Error("useProject must be used within a ProjectProvider");
    }
    return value;
}

export function ProjectProvider(props: React.PropsWithChildren<{ projectId: string; fallback?: ReactElement }>) {
    const query = useGetProjectQuery(props.projectId);

    if (!query.data) {
        return props.fallback ?? <FullScreenLoader />;
    }

    const getWorkstream = (workstreamId: string) => {
        const ws = query.data!.outline.workstreams.find((w) => w.id === workstreamId);
        if (!ws) {
            throw new Error(`Workstream with id "${workstreamId}" does not exist in project "${query.data!.id}"`);
        }
        return ws;
    };
    const getDeliverable = (workstreamId: string, deliverableId: string) => {
        const ws = getWorkstream(workstreamId);
        const deliverable = ws.deliverables.find((d) => d.id === deliverableId);
        if (!deliverable) {
            throw new Error(`Deliverable with id "${deliverableId}" does not exist in workstream "${workstreamId}"`);
        }
        return deliverable;
    };
    const context = {
        project: query.data,
        refetch: async () => {
            await query.refetch();
        },
        getWorkstream,
        getDeliverable,
    };

    return <ProjectContext.Provider value={context}>{props.children}</ProjectContext.Provider>;
}
